import {useCallback, useEffect, useMemo, useState} from "react";
import {
	App as AntdApp,
	Button,
	Card,
	Col,
	Form,
	Row,
	Space,
	Spin,
	Tabs,
	Tag,
	Typography
} from "antd";
import {
	AppstoreOutlined,
	FileImageOutlined,
	FileTextOutlined,
	ReloadOutlined,
	RobotOutlined,
	SaveOutlined,
	SettingOutlined
} from "@ant-design/icons";
import dayjs from "dayjs";

import {fetchSettings, saveSettings} from "../api/settingsApi.js";
import {normalizeWebpProfiles} from "../utils/webpProfiles.js";
import WebpSettingsTab from "../components/WebpSettingsTab.jsx";
import AiSettingsTab from "../components/AiSettingsTab.jsx";
import AiDescriptionsTab from "../components/AiDescriptionsTab.jsx";
import BannerDescriptionsTab from "../components/BannerDescriptionsTab.jsx";
import FieldLayoutTab from "../components/FieldLayoutTab.jsx";
import "./SettingsPage.css";

const {Title, Paragraph} = Typography;

/**
 * Valori di default del form
 */
function getDefaultFormValues()
{
	return {
		ai: {
			apiKey: "",
			defaultModel: "gpt-5-nano",
			systemPrompt: ""
		},
		webp: {
			quality: 80,
			effort: 4,
			vectorDensityMode: "auto",
			vectorDensityDpi: 300
		},
		webpProfiles: [],
		flyerPages: {
			format: "webp",
			jpegQuality: 85,
			ocrDpi: 200,
			exportDpi: 72
		}
	};
}

/**
 * Normalizza i dati dal backend per il form
 */
function prepareFormValues(settings)
{
	const base = getDefaultFormValues();
	const incoming = settings || {};

	if (incoming.ai && typeof incoming.ai === "object")
	{
		base.ai.apiKey = incoming.ai.apiKey || "";
		base.ai.defaultModel = incoming.ai.defaultModel || base.ai.defaultModel;
		base.ai.systemPrompt = incoming.ai.systemPrompt || "";
	}

	if (incoming.webp && typeof incoming.webp === "object")
	{
		base.webp.quality = typeof incoming.webp.quality === "number"
			? incoming.webp.quality
			: base.webp.quality;
		base.webp.effort = typeof incoming.webp.effort === "number"
			? incoming.webp.effort
			: base.webp.effort;
		base.webp.vectorDensityMode = incoming.webp.vectorDensityMode === "manual" ? "manual" : "auto";
		base.webp.vectorDensityDpi = typeof incoming.webp.vectorDensityDpi === "number"
			? incoming.webp.vectorDensityDpi
			: base.webp.vectorDensityDpi;
	}

	if (Array.isArray(incoming.webpProfiles))
	{
		base.webpProfiles = normalizeWebpProfiles(incoming.webpProfiles);
	}

	if (incoming.flyerPages && typeof incoming.flyerPages === "object")
	{
		base.flyerPages.format = (incoming.flyerPages.format === "jpg" || incoming.flyerPages.format === "jpeg") ? "jpg" : "webp";
		base.flyerPages.jpegQuality = typeof incoming.flyerPages.jpegQuality === "number"
			? incoming.flyerPages.jpegQuality
			: 85;
		base.flyerPages.ocrDpi = typeof incoming.flyerPages.ocrDpi === "number"
			? incoming.flyerPages.ocrDpi
			: 200;
		base.flyerPages.exportDpi = typeof incoming.flyerPages.exportDpi === "number"
			? incoming.flyerPages.exportDpi
			: 72;
	}

	return base;
}

/**
 * Costruisce il payload da inviare al backend
 */
function buildPayload(values)
{
	const payload = {};

	if (values.ai && typeof values.ai === "object")
	{
		payload.ai = {
			apiKey: typeof values.ai.apiKey === "string" ? values.ai.apiKey.trim() : "",
			defaultModel: values.ai.defaultModel?.trim() || "gpt-5-nano",
			systemPrompt: typeof values.ai.systemPrompt === "string" ? values.ai.systemPrompt : ""
		};
	}

	if (values.webp && typeof values.webp === "object")
	{
		const quality = Number(values.webp.quality);
		const effort = Number(values.webp.effort);
		const vectorDensityDpi = Number(values.webp.vectorDensityDpi);

		payload.webp = {
			quality: Number.isFinite(quality) ? quality : 80,
			effort: Number.isFinite(effort) ? effort : 4,
			vectorDensityMode: values.webp.vectorDensityMode === "manual" ? "manual" : "auto",
			vectorDensityDpi: Number.isFinite(vectorDensityDpi) ? vectorDensityDpi : 300
		};
	}

	if (Array.isArray(values.webpProfiles))
	{
		payload.webpProfiles = normalizeWebpProfiles(values.webpProfiles);
	}

	if (values.flyerPages && typeof values.flyerPages === "object")
	{
		const jpegQuality = Number(values.flyerPages.jpegQuality);
		const ocrDpi = Number(values.flyerPages.ocrDpi);
		const exportDpi = Number(values.flyerPages.exportDpi);
		payload.flyerPages = {
			format: values.flyerPages.format === "jpg" ? "jpg" : "webp",
			jpegQuality: Number.isFinite(jpegQuality) ? jpegQuality : 85,
			ocrDpi: Number.isFinite(ocrDpi) ? ocrDpi : 200,
			exportDpi: Number.isFinite(exportDpi) ? exportDpi : 72
		};
	}

	return payload;
}

/**
 * Confronta due oggetti per verificare modifiche
 */
function hasChanges(original, current)
{
	if (!original || !current)
		return false;

	const orig = prepareFormValues(original);

	// Confronto AI
	if (orig.ai.apiKey !== (current.ai?.apiKey || ""))
		return true;
	if (orig.ai.defaultModel !== (current.ai?.defaultModel || ""))
		return true;
	if (orig.ai.systemPrompt !== (current.ai?.systemPrompt || ""))
		return true;

	// Confronto WebP
	if (orig.webp.quality !== current.webp?.quality)
		return true;
	if (orig.webp.effort !== current.webp?.effort)
		return true;
	if (orig.webp.vectorDensityMode !== (current.webp?.vectorDensityMode === "manual" ? "manual" : "auto"))
		return true;
	if (orig.webp.vectorDensityDpi !== current.webp?.vectorDensityDpi)
		return true;

	// Confronto webpProfiles
	const origProfiles = JSON.stringify(orig.webpProfiles || []);
	const currentProfiles = JSON.stringify(current.webpProfiles || []);
	if (origProfiles !== currentProfiles)
		return true;

	// Confronto flyerPages
	if (orig.flyerPages?.format !== (current.flyerPages?.format ?? "webp"))
		return true;
	if (orig.flyerPages?.jpegQuality !== (current.flyerPages?.jpegQuality ?? 85))
		return true;
	if (orig.flyerPages?.ocrDpi !== (current.flyerPages?.ocrDpi ?? 200))
		return true;
	if (orig.flyerPages?.exportDpi !== (current.flyerPages?.exportDpi ?? 72))
		return true;

	return false;
}

export default function SettingsPage()
{
	const {message} = AntdApp.useApp();
	const [form] = Form.useForm();

	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [settings, setSettings] = useState(null);
	const [isDirty, setIsDirty] = useState(false);
	const [activeTab, setActiveTab] = useState(() =>
	{
		try
		{
			return localStorage.getItem("dash.settings.tab") || "webp";
		}
		catch
		{
			return "webp";
		}
	});

	const lastUpdated = useMemo(() =>
	{
		if (!settings?.updatedAt)
			return null;

		return dayjs(settings.updatedAt).format("DD/MM/YYYY [alle] HH:mm");
	}, [settings]);

	const load = useCallback(async () =>
	{
		setLoading(true);
		try
		{
			const data = await fetchSettings();
			const merged = prepareFormValues(data);
			setSettings(data);
			form.setFieldsValue(merged);
			setIsDirty(false);
		}
		catch (error)
		{
			message.error(error?.response?.data?.error || "Impossibile caricare le impostazioni");
			form.setFieldsValue(getDefaultFormValues());
		}
		finally
		{
			setLoading(false);
		}
	}, [form, message]);

	useEffect(() =>
	{
		load();
	}, [load]);

	const handleValuesChange = useCallback(() =>
	{
		const current = form.getFieldsValue(true);
		setIsDirty(hasChanges(settings, current));
	}, [form, settings]);

	const handleSubmit = useCallback(async (values) =>
	{
		setSaving(true);
		try
		{
			const payload = buildPayload(values);
			const saved = await saveSettings(payload);
			const merged = prepareFormValues(saved);
			setSettings(saved);
			form.setFieldsValue(merged);
			setIsDirty(false);
			message.success("Impostazioni salvate con successo");
		}
		catch (error)
		{
			message.error(error?.response?.data?.error || "Salvataggio non riuscito");
		}
		finally
		{
			setSaving(false);
		}
	}, [form, message]);

	const handleReload = useCallback(() =>
	{
		if (loading)
			return;

		if (isDirty)
		{
			message.warning("Modifiche non salvate: ricarico i dati originali");
		}
		load();
	}, [load, loading, isDirty, message]);

	const handleTabChange = useCallback((key) =>
	{
		setActiveTab(key);
		try
		{
			localStorage.setItem("dash.settings.tab", key);
		}
		catch
		{
			// Ignora errori localStorage
		}
	}, []);

	// Configurazione tabs
	const tabItems = useMemo(() => [
		{
			key: "webp",
			label: (
				<span className="settings-tab-label">
					<FileImageOutlined className="settings-tab-icon webp"/>
					<span className="settings-tab-text">Conversione immagini</span>
				</span>
			),
			children: (
				<WebpSettingsTab
					loading={loading}
					saving={saving}
					isDirty={isDirty}
					onReload={handleReload}
				/>
			)
		},
		{
			key: "ai",
			label: (
				<span className="settings-tab-label">
					<RobotOutlined className="settings-tab-icon ai"/>
					<span className="settings-tab-text">Intelligenza artificiale</span>
				</span>
			),
			children: (
				<AiSettingsTab
					loading={loading}
					saving={saving}
					isDirty={isDirty}
					onReload={handleReload}
				/>
			)
		},
		{
			key: "ai-descriptions",
			label: (
				<span className="settings-tab-label">
					<FileTextOutlined className="settings-tab-icon ai"/>
					<span className="settings-tab-text">Descrizione AI gabbie</span>
				</span>
			),
			children: <AiDescriptionsTab/>
		},
		{
			key: "field-layout",
			label: (
				<span className="settings-tab-label">
					<AppstoreOutlined className="settings-tab-icon ai"/>
					<span className="settings-tab-text">Posizione campi</span>
				</span>
			),
			children: <FieldLayoutTab/>
		},
		{
			key: "banner-descriptions",
			label: (
				<span className="settings-tab-label">
					<FileTextOutlined className="settings-tab-icon ai"/>
					<span className="settings-tab-text">Descrizione banner</span>
				</span>
			),
			children: <BannerDescriptionsTab/>
		}
	], [loading, saving, isDirty, handleReload]);

	return (
		<Form
			form={form}
			layout="vertical"
			onFinish={handleSubmit}
			onValuesChange={handleValuesChange}
			initialValues={getDefaultFormValues()}
			className="settings-page"
			disabled={saving}
		>
			<Space direction="vertical" size={24} style={{width: "100%"}}>
				{/* Header Card */}
				<Card className="settings-header card-elev">
					<div className="settings-header-content">
						<SettingOutlined className="settings-header-icon"/>
						<div className="settings-header-info">
							<Title level={4} className="settings-header-title">
								Impostazioni
							</Title>
							<Paragraph className="settings-header-description">
								Configurazioni centrali per AI e conversione immagini
							</Paragraph>
							<div className="settings-header-meta">
								{lastUpdated && (
									<Tag color="default">
										Aggiornato: {lastUpdated}
									</Tag>
								)}
								{isDirty && (
									<Tag color="warning">
										Modifiche non salvate
									</Tag>
								)}
							</div>
						</div>
					</div>
				</Card>

				{/* Tabs con contenuto */}
				<Spin spinning={loading} wrapperClassName="settings-spin-container">
					<Tabs
						activeKey={activeTab}
						onChange={handleTabChange}
						type="card"
						className="settings-tabs"
						items={tabItems}
						destroyOnHidden={false}
					/>
				</Spin>
			</Space>
		</Form>
	);
}
